import { Injectable, NgModule } from '@angular/core';
import {
  PreloadingStrategy,
  Route,
  RouterModule,
  Routes,
} from '@angular/router';
import { LandingComponent } from '../landing/landing.component';
import { PageNotFoundComponent } from '../page-not-found/page-not-found.component';
import { BaseLayoutComponent } from '../base-layout/base-layout.component';
import { AuthGuard } from './auth.guard';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  hdrgKrankenkassenUserGuard,
  krankenkassenUserGuard,
} from './krankenkassen-user.guard';
import { NotificationOverviewComponent } from '../notifications/components/notification-overview/notification-overview.component';
import { NotificationDetailComponent } from '../notifications/components/notification-detail/notification-detail.component';

const routes: Routes = [
  {
    path: '',
    component: BaseLayoutComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        component: LandingComponent,
        data: {
          breadcrumb: 'Öffentliche Startseite',
        },
      },
      {
        path: 'registrierung',
        loadChildren: () =>
          import('../registration/registration.module').then(
            (m) => m.RegistrationModule,
          ),
        data: {
          breadcrumb: 'Registrierung',
        },
      },
      {
        path: 'reset_password/verify',
        loadChildren: () =>
          import('../reset-password/reset-password.module').then(
            (m) => m.ResetPasswordModule,
          ),
        data: {
          breadcrumb: 'Passwort Zurücksetzung',
        },
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('../dashboard/dashboard.module').then(
            (m) => m.DashboardModule,
          ),
        canActivate: [AuthGuard],
        data: {
          preload: true,
          breadcrumb: 'Startseite',
        },
      },
      {
        path: '',
        loadChildren: () =>
          import('../lex-online/lex-online.module').then(
            (m) => m.LexOnlineModule,
          ),
        canActivate: [AuthGuard],
        data: {
          breadcrumb: 'Lex Online',
        },
      },
      {
        path: '',
        loadChildren: () =>
          import('../kontakt-und-hilfe/kontakt-und-hilfe.module').then(
            (m) => m.KontaktUndHilfeModule,
          ),
        canActivate: [AuthGuard],
        data: {
          breadcrumb: 'Kontakt & Hilfe',
        },
      },
      {
        path: '',
        loadChildren: () =>
          import('../stammdaten/stammdaten.module').then(
            (m) => m.StammdatenModule,
          ),
        canActivate: [AuthGuard],
        data: {
          preload: true,
          breadcrumb: 'Stammdaten',
        },
      },
      {
        path: '',
        loadChildren: () =>
          import('../dokumente-und-vertraege/dokumente-und-vertraege.module').then(
            (m) => m.DokumenteUndVertraegeModule,
          ),
        canActivate: [AuthGuard],
        data: {
          preload: true,
          breadcrumb: 'Dokumente und Verträge',
        },
      },
      {
        path: '',
        loadChildren: () =>
          import('../versorgungsvertraege/versorgungsvertraege.module').then(
            (m) => m.VersorgungsvertraegeModule,
          ),
        canActivate: [AuthGuard, krankenkassenUserGuard],
        data: {
          breadcrumb: 'Versorgungsverträge',
        },
      },
      {
        path: '',
        loadChildren: () =>
          import('../hdrg/hdrg.module').then((m) => m.HDRGModule),
        canActivate: [AuthGuard, hdrgKrankenkassenUserGuard],
        data: {
          breadcrumb: 'HDRG',
        },
      },
      {
        path: '',
        loadChildren: () =>
          import('../arzt-liste/arzt-liste.module').then(
            (m) => m.ArztListeModule,
          ),
        canActivate: [AuthGuard],
        data: {
          breadcrumb: 'Arzt-Liste',
        },
      },
      {
        path: '',
        loadChildren: () =>
          import('../patienten-selbst-einschreibung/patienten-selbst-einschreibung.module').then(
            (m) => m.PatientenSelbstEinschreibungModule,
          ),
        data: {
          breadcrumb: 'Selbst-Einschreibung',
        },
      },
      {
        path: 'notifications',
        component: NotificationOverviewComponent,
        canActivate: [AuthGuard],
        data: {
          breadcrumb: 'Benachrichtigungen',
        },
      },
      {
        path: 'notifications/:id',
        component: NotificationDetailComponent,
        canActivate: [AuthGuard],
        data: {
          breadcrumb: 'Details',
        },
      },
      { path: 'register', redirectTo: 'registrierung' },
      { path: '**', component: PageNotFoundComponent },
    ],
  },
]; // sets up routes constant where you define your routes

@Injectable({ providedIn: 'root' })
class MyPreloadingStrategy extends PreloadingStrategy {
  preload(route: Route, fn: () => Observable<unknown>): Observable<unknown> {
    if (route.data && route.data.preload) {
      return fn().pipe(catchError(() => of(null as unknown)));
    }
    return of(null as unknown);
  }
}

// configures NgModule imports and exports
@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: MyPreloadingStrategy }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
